
import { useEffect, useState } from 'react';

interface IntroAnimationProps {
  onComplete: () => void;
}

export default function IntroAnimation({ onComplete }: IntroAnimationProps) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timings = [
      800,  // Initial delay
      2000, // Logo fade in
      3500, // Tagline appears
      5000  // Complete and fade out
    ];

    const timer = setTimeout(() => {
      if (step < timings.length - 1) {
        setStep(step + 1);
      } else {
        onComplete();
      }
    }, step === 0 ? timings[0] : timings[step] - timings[step - 1]);

    return () => clearTimeout(timer);
  }, [step, onComplete]);

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-[#0a0a0a] via-gray-950 to-[#0a0a0a] flex items-center justify-center overflow-hidden">
      
      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl transition-all duration-2000 ${step >= 1 ? 'opacity-100 scale-150' : 'opacity-0 scale-50'}`}></div>
        <div className={`absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-600/15 rounded-full blur-3xl transition-all duration-2000 delay-300 ${step >= 1 ? 'opacity-100 scale-150' : 'opacity-0 scale-50'}`}></div>
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-amber-500/10 to-amber-600/10 rounded-full blur-3xl transition-all duration-2000 delay-150 ${step >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}></div>
      </div>

      {/* Radial lines effect */}
      <div className={`absolute inset-0 transition-opacity duration-1500 ${step >= 1 ? 'opacity-20' : 'opacity-0'}`}>
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute top-1/2 left-1/2 w-px h-1/2 bg-gradient-to-b from-amber-500/50 to-transparent origin-top"
            style={{
              transform: `translate(-50%, -100%) rotate(${i * 30}deg)`,
              animationDelay: `${i * 0.1}s`
            }}
          ></div>
        ))}
      </div>

      {/* Main content container */}
      <div className="relative z-10 text-center px-6">
        
        {/* Logo monogram with glow */}
        <div className={`mb-8 transition-all duration-1500 ${step >= 1 ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-75 translate-y-10'}`}>
          <div className="relative inline-block">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-amber-600 blur-3xl opacity-50 scale-150"></div>
            
            {/* Monogram */}
            <div className="relative">
              <div className="w-32 h-32 mx-auto mb-6 flex items-center justify-center border-4 border-amber-500/50 rounded-full bg-gradient-to-br from-amber-500/10 to-amber-600/10 backdrop-blur-sm shadow-2xl shadow-amber-500/30">
                <span className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  RT
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main brand name */}
        <div className={`transition-all duration-1500 delay-300 ${step >= 1 ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-90 translate-y-10'}`}>
          <h1 className="text-7xl lg:text-8xl font-bold tracking-[0.15em] mb-4 text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-amber-500 to-amber-300 drop-shadow-2xl" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            RINARD
          </h1>
          <h2 className="text-5xl lg:text-6xl font-light tracking-[0.3em] text-transparent bg-clip-text bg-gradient-to-r from-gray-300 via-white to-gray-300" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            TAILORS
          </h2>
        </div>

        {/* Decorative line */}
        <div className={`flex items-center justify-center my-8 transition-all duration-1500 delay-500 ${step >= 2 ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}>
          <div className="h-px w-16 bg-gradient-to-r from-transparent via-amber-500 to-transparent"></div>
          <div className="w-2 h-2 mx-4 bg-amber-500 rounded-full shadow-lg shadow-amber-500/50"></div>
          <div className="h-px w-16 bg-gradient-to-r from-transparent via-amber-500 to-transparent"></div>
        </div>

        {/* Tagline */}
        <div className={`transition-all duration-1500 delay-700 ${step >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
          <p className="text-lg lg:text-xl uppercase tracking-[0.4em] text-gray-400 font-light mb-2">
            Specialist in Gents
          </p>
          <p className="text-sm lg:text-base uppercase tracking-[0.3em] text-amber-500/80 font-light">
            Modern Gentswear · Suits · Coats · Shirts · Shoes
          </p>
        </div>

        {/* Subtitle with location */}
        <div className={`mt-8 transition-all duration-1500 delay-1000 ${step >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
          <p className="text-sm text-gray-500 tracking-wider">
            <i className="ri-map-pin-line mr-2 text-amber-500"></i>
            Hendala, Wattala · Sri Lanka
          </p>
        </div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 bg-amber-400/40 rounded-full transition-opacity duration-1000 ${step >= 1 ? 'animate-pulse' : 'opacity-0'}`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 4}s`
            }}
          ></div>
        ))}
      </div>

      {/* Fade out overlay */}
      <div className={`absolute inset-0 bg-[#0a0a0a] transition-opacity duration-1000 ${step >= 3 ? 'opacity-100' : 'opacity-0'}`}></div>
    </div>
  );
}
